import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthProvider";

export const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

const defaultPrefs = (columns) => ({
  columns: columns.filter((c) => c.defaultVisible).map((c) => c.id),
  sort: null, // { id, dir: "asc" | "desc" } ; null = newest first
  pageSize: DEFAULT_PAGE_SIZE,
});

const sanitize = (raw, columns) => {
  const fallback = defaultPrefs(columns);
  if (!raw || typeof raw !== "object") return fallback;
  const known = new Set(columns.map((c) => c.id));
  const visible = Array.isArray(raw.columns)
    ? raw.columns.filter((id) => known.has(id))
    : fallback.columns;
  const sortable = new Set([...known, "fav"]);
  const sort =
    raw.sort && sortable.has(raw.sort.id) && ["asc", "desc"].includes(raw.sort.dir)
      ? { id: raw.sort.id, dir: raw.sort.dir }
      : null;
  const pageSize = PAGE_SIZES.includes(raw.pageSize) ? raw.pageSize : DEFAULT_PAGE_SIZE;
  return { columns: visible.length ? visible : fallback.columns, sort, pageSize };
};

// Per-user table preferences (visible columns, sort, page size) for one
// table scope, persisted in localStorage under mysolon.ds.<scope>.<uuid>.
export default function useTablePrefs(scope, columns) {
  const { auth } = useContext(AuthContext);
  const storageKey = `mysolon.ds.${scope}.${auth?.uuid ?? "anon"}`;

  const load = useCallback(() => {
    try {
      return sanitize(JSON.parse(localStorage.getItem(storageKey)), columns);
    } catch {
      return defaultPrefs(columns);
    }
  }, [storageKey, columns]);

  const [prefs, setPrefs] = useState(load);

  useEffect(() => {
    setPrefs(load());
  }, [load]);

  const update = useCallback(
    (patch) => {
      setPrefs((prev) => {
        const next = sanitize(
          { ...prev, ...(typeof patch === "function" ? patch(prev) : patch) },
          columns
        );
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    [storageKey, columns]
  );

  const actions = useMemo(
    () => ({
      toggleColumn: (id) =>
        update((prev) => {
          const visible = prev.columns.includes(id)
            ? prev.columns.filter((c) => c !== id)
            : // keep the canonical column order
              columns.map((c) => c.id).filter((c) => c === id || prev.columns.includes(c));
          return { columns: visible };
        }),
      resetColumns: () => update({ columns: defaultPrefs(columns).columns }),
      // Header click cycles asc → desc → default (newest first).
      cycleSort: (id) =>
        update((prev) => {
          if (prev.sort?.id !== id) return { sort: { id, dir: "asc" } };
          if (prev.sort.dir === "asc") return { sort: { id, dir: "desc" } };
          return { sort: null };
        }),
      setPageSize: (pageSize) => update({ pageSize }),
    }),
    [update, columns]
  );

  return { prefs, ...actions };
}
